// Shared content for the home page and the /projects page.

// The four layers of the "exploded stack". Numbers count up from the
// bottom of the stack (01 Platform) to the top (04 Mobile).
export const layers = [
  {
    num: '04',
    name: 'Mobile',
    tools: 'Flutter · Ionic',
    desc: 'Cross-platform iOS and Android apps from a single Flutter codebase — offline-first, synced in real time with Firebase or Supabase, and published to the stores. The layer people actually hold in their hand.',
    features: ['Flutter & Ionic', 'Firebase & Supabase', 'App Store & Google Play'],
  },
  {
    num: '03',
    name: 'Interface',
    tools: 'Angular · React · Vue',
    desc: 'Front ends in Angular, React and Vue: dashboards, portals and web apps that stay fast and clear as they grow.',
    features: ['Angular, React & Vue', 'TypeScript', 'Responsive, mobile-first UI'],
  },
  {
    num: '02',
    name: 'Services',
    tools: 'Laravel · Node.js',
    desc: 'REST APIs, authentication, payments and the data underneath, built on Laravel and Node.js — the plumbing that connects the web app, the mobile app and everything else.',
    features: ['REST API design', 'Auth, payments & webhooks', 'MySQL, PostgreSQL & MongoDB', 'Socket.IO real-time'],
  },
  {
    num: '01',
    name: 'Platform',
    tools: 'WordPress',
    desc: 'Custom WordPress plugins and themes that push a site past what its page builder can do — from booking systems to licensed commercial plugins. Built to be maintained, not patched.',
    features: ['Custom plugins', 'Theme development', 'API integration'],
  },
];

export const LAYER_ORDER = ['04', '03', '02', '01'];

export const categoryLabel = { web: 'Web', mobile: 'Mobile', freelance: 'Freelance' };

export const projects = [
  {
    id: 'transactfy',
    title: 'Transactfy',
    subtitle: 'POS and Inventory',
    summary: 'Offline-first point of sale and inventory app for Philippine businesses — multi-branch, barcode checkout, receipt printing. Live on Google Play.',
    description: 'All-in-one point of sale and inventory app for Philippine businesses — fast barcode checkout, multi-branch management, staff roles, thermal receipt printing and real-time reports.',
    fullDescription: 'Transactfy is a full-featured point of sale and inventory system I designed and built end-to-end with Flutter, published on Google Play under ZCA Softwares. It is offline-first — cashiers keep selling without internet and everything syncs when back online — and supports multiple branches from one account with role-based access for owners, admins and cashiers. The backend runs on Supabase (PostgreSQL, Auth, Realtime, Storage) with Hive for local storage, PayMongo for subscriptions (GCash, GrabPay, Maya, cards), Bluetooth/thermal receipt printing for 58mm and 80mm printers, and camera or hardware barcode scanning. Ships with a 30-day free trial and tiered Starter / Business / Enterprise plans.',
    technologies: ['Flutter', 'Dart', 'Riverpod', 'Supabase', 'PostgreSQL', 'Hive', 'PayMongo', 'Bluetooth Printing'],
    layers: ['04', '02'],
    category: 'mobile',
    year: '2026',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.zca.zca_pos',
    liveLabel: 'Google Play',
    liveButton: 'Get it on Google Play',
    liveIcon: 'fa-brands fa-google-play',
    images: [
      '/images/projects/transactfy-cover.jpg',
      '/images/projects/transactfy-1.jpg',
      '/images/projects/transactfy-2.jpg',
      '/images/projects/transactfy-3.jpg',
      '/images/projects/transactfy-4.jpg',
      '/images/projects/transactfy-5.jpg',
      '/images/projects/transactfy-6.jpg',
    ],
    portrait: true,
    features: [
      'Fast POS checkout with barcode scanning (camera or hardware scanner)',
      'Cash, GCash, GrabPay, Maya and card payments',
      'Offline-first with automatic sync when back online',
      'Real-time inventory tracking with low-stock alerts',
      'Multi-branch management from a single account',
      'Role-based access: owner, admin, cashier',
      'Bluetooth / thermal receipt printing (58mm & 80mm)',
      'Daily sales summaries, revenue trends and analytics',
      'Real-time notifications for sales, stock and team updates',
      '27+ currencies supported',
      'Subscription billing via PayMongo with 30-day free trial',
      'Published on Google Play',
    ],
  },
  {
    id: 'ridewatch',
    title: 'RideWatch',
    subtitle: 'School bus tracking',
    summary: 'School bus tracking platform — I built the website plus the Parent and Driver apps, both live on the App Store and Google Play.',
    description: 'School bus tracking platform: the marketing website plus the Parent and Driver mobile apps — both live on the App Store and Google Play — with real-time GPS tracking, boarding alerts and route management.',
    fullDescription: 'RideWatch lets parents know exactly where their child\'s school bus is. I delivered the whole product surface for the client: the public website, the My Ridewatch parent app that shows the bus live on a map and sends instant notifications when a child boards, arrives or is delayed, and the My Ridewatch Driver app for selecting routes, tracking stops and broadcasting location in real time. Both apps are built with Flutter on a Firebase backend with Google Maps integration, and both are published on the App Store and Google Play.',
    technologies: ['Flutter', 'Dart', 'Firebase', 'Google Maps API', 'HTML5', 'CSS3', 'JavaScript'],
    layers: ['04', '03', '02'],
    category: 'freelance',
    categories: ['freelance', 'mobile'],
    year: '2025 – 2026',
    liveUrl: 'https://ridewatch.org/',
    storeLinks: [
      { label: 'Parent App · Google Play', icon: 'fa-brands fa-google-play', url: 'https://play.google.com/store/apps/details?id=com.riderwatch.trackerUser' },
      { label: 'Parent App · App Store', icon: 'fa-brands fa-apple', url: 'https://apps.apple.com/us/app/my-ridewatch/id6755679759' },
      { label: 'Driver App · Google Play', icon: 'fa-brands fa-google-play', url: 'https://play.google.com/store/apps/details?id=com.riderwatch.trackerDriver' },
      { label: 'Driver App · App Store', icon: 'fa-brands fa-apple', url: 'https://apps.apple.com/us/app/my-ridewatch-driver/id6755679971' },
    ],
    images: [
      '/images/projects/ridewatch-site-1.jpg',
      '/images/projects/ridewatch-2.jpeg',
      '/images/projects/ridewatch-1.jpeg',
    ],
    features: [
      'Parent app: live bus location on a map',
      'Boarding, arrival and delay push notifications',
      'Driver app: route selection and stop tracking',
      'Real-time GPS broadcasting from the driver device',
      'Published on both the App Store and Google Play',
      'Firebase authentication and realtime backend',
      'Cross-platform (iOS & Android) with Flutter',
      'Marketing website with pricing and download funnels',
    ],
  },
  {
    id: 'staysuiteph',
    title: 'StaySuitePH',
    subtitle: 'Vacation rentals',
    summary: 'Vacation rental platform for verified homes, condos and villas across the Philippines, with search and direct booking.',
    description: 'Vacation rental platform for verified homes, condos and villas across the Philippines — search by destination, dates and guests, with direct booking from local hosts.',
    fullDescription: 'StaySuitePH is a vacation rental marketplace for the Philippines. Guests can search verified homes, condos and villas by destination, dates and guest count, browse featured stays, and book directly with local hosts with real-time availability. The platform also includes a hosting mode so property owners can list and manage their own stays.',
    technologies: ['WordPress', 'PHP', 'MySQL', 'JavaScript', 'Booking System'],
    layers: ['02', '01'],
    category: 'freelance',
    year: '2025',
    liveUrl: 'https://staysuiteph.com/',
    images: ['/images/projects/staysuiteph-1.jpg'],
    features: [
      'Destination / date / guest search',
      'Featured stays and property pages',
      'Real-time availability and direct booking',
      'Host mode for property owners',
      'Sign-in and account management',
      'Blog and content pages',
    ],
  },
  {
    id: 'tessabgccondo',
    title: "Tessa's BGC Condo",
    subtitle: 'Luxury rentals',
    summary: 'Luxury short-term rental site for handpicked condos in Bonifacio Global City, with listings and booking enquiries.',
    description: 'Luxury short-term rental website showcasing handpicked condos in Bonifacio Global City, Manila, with property listings and booking enquiries.',
    fullDescription: "Tessa's BGC Condo offers premium short-term rentals in Bonifacio Global City for business and leisure travellers. I delivered a WordPress site with a custom theme: property showcase pages with photo galleries and amenities, a clear booking enquiry flow, and a polished, mobile-first design that reflects the brand's upscale positioning.",
    technologies: ['WordPress', 'PHP', 'Custom Theme', 'JavaScript', 'Responsive Design'],
    layers: ['03', '01'],
    category: 'freelance',
    year: '2025',
    liveUrl: 'https://tessabgccondo.com/',
    images: ['/images/projects/tessabgccondo-1.jpg'],
    features: [
      'Custom WordPress theme',
      'Property listing pages with galleries',
      'Amenities and location highlights',
      'Booking enquiry flow',
      'Mobile-first responsive design',
      'SEO-friendly structure',
    ],
  },
];

export const contact = {
  email: 'benjomabrasado@gmail.com',
  phone: '+639157521601',
  phoneLabel: '(+63) 915 752 1601',
  github: 'https://github.com/benjoabrasado25',
  linkedin: 'https://www.linkedin.com/in/benjo-abrasado-002a8416a/',
};
